import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { UploadCloud, X, FileText, Loader2, CheckCircle2, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PROJECT_TYPES, BUDGETS, TIMEFRAMES } from "../constants/content";

const MAX_MB = 30;
const ALLOWED = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx"];

const fmtSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const inputCls = "w-full border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-slate2/50 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";
const labelCls = "block font-mono text-[11px] tracking-[0.15em] uppercase text-slate2 mb-2";

export default function EnquiryForm({ defaultType = "" }) {
  const [fields, setFields] = useState({ name: "", email: "", phone: "", address: "", project_type: defaultType, budget: "", timeframe: "", message: "", website: "" });
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState("form");
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setFields((f) => ({ ...f, project_type: defaultType })); }, [defaultType]);

  const set = (k) => (e) => setFields({ ...fields, [k]: e.target.value });

  const addFiles = (list) => {
    const next = [...files];
    for (const f of Array.from(list)) {
      const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
      if (!ALLOWED.includes(ext)) { toast.error(`${f.name}: only PDF, JPG, PNG, DOC, DOCX, XLS and XLSX files are accepted`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} is larger than ${MAX_MB}MB`); continue; }
      if (next.length >= 10) { toast.error("Maximum 10 files per enquiry"); break; }
      if (next.some((x) => x.file.name === f.name && x.file.size === f.size)) continue;
      next.push({ file: f, id: Math.random().toString(36).slice(2) });
    }
    setFiles(next);
  };

  const removeFile = (id) => setFiles(files.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!fields.name.trim() || !fields.email.trim() || !fields.phone.trim()) {
      toast.error("Please fill in your name, email and phone number");
      return;
    }
    setPhase("sending");
    setProgress(0);
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append("files", f.file));
    try {
      const res = await api.post("/enquiries", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      setProgress(100);
      setPhase("done");
      if (!res.data?.email_sent) {
        toast.warning("Your enquiry was received, but the email notification may be delayed. We'll still see it.");
      }
    } catch (err) {
      setPhase("form");
      toast.error(formatApiError(err) || "Sending failed. Please try again or email razorbuild@outlook.com directly.");
    }
  };

  if (phase === "done") {
    return (
      <div data-testid="enquiry-success" className="bg-white border border-line p-8 md:p-12 text-center">
        <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-5" />
        <h3 className="font-display font-bold text-2xl md:text-3xl text-ink mb-3">Thanks for contacting Razor Build</h3>
        <p className="text-slate2 leading-relaxed max-w-md mx-auto">
          We've received your project details{files.length > 0 ? " and the plans you uploaded" : ""}. We'll review the information and get in touch to discuss the next steps.
        </p>
        <button
          onClick={() => { setPhase("form"); setFields({ name: "", email: "", phone: "", address: "", project_type: "", budget: "", timeframe: "", message: "", website: "" }); setFiles([]); }}
          data-testid="send-another-enquiry"
          className="mt-8 font-mono text-[11px] tracking-[0.15em] uppercase border border-ink px-5 py-3 hover:bg-ink hover:text-white transition-colors"
        >
          Send Another Enquiry
        </button>
      </div>
    );
  }

  const sending = phase === "sending";

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="bg-white border border-line p-6 md:p-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="enq-name" className={labelCls}>Name*</label>
          <input id="enq-name" data-testid="enquiry-name" required value={fields.name} onChange={set("name")} className={inputCls} placeholder="Your full name" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="enq-email" className={labelCls}>Email*</label>
          <input id="enq-email" data-testid="enquiry-email" type="email" required value={fields.email} onChange={set("email")} className={inputCls} placeholder="you@example.co.nz" autoComplete="email" />
        </div>
        <div>
          <label htmlFor="enq-phone" className={labelCls}>Phone*</label>
          <input id="enq-phone" data-testid="enquiry-phone" type="tel" required value={fields.phone} onChange={set("phone")} className={inputCls} placeholder="021 000 0000" autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="enq-address" className={labelCls}>Project Address / Suburb</label>
          <input id="enq-address" data-testid="enquiry-address" value={fields.address} onChange={set("address")} className={inputCls} placeholder="e.g. Karori, Wellington" />
        </div>
        <div>
          <label htmlFor="enq-type" className={labelCls}>What are you planning?</label>
          <select id="enq-type" data-testid="enquiry-project-type" value={fields.project_type} onChange={set("project_type")} className={inputCls}>
            <option value="">Select an option</option>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="enq-budget" className={labelCls}>Estimated Project Budget</label>
          <select id="enq-budget" data-testid="enquiry-budget" value={fields.budget} onChange={set("budget")} className={inputCls}>
            <option value="">Select a range</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="enq-timeframe" className={labelCls}>When are you hoping to start?</label>
          <select id="enq-timeframe" data-testid="enquiry-timeframe" value={fields.timeframe} onChange={set("timeframe")} className={inputCls}>
            <option value="">Select a timeframe</option>
            {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="enq-message" className={labelCls}>Tell Us About Your Project</label>
          <textarea id="enq-message" data-testid="enquiry-message" rows={5} value={fields.message} onChange={set("message")} className={`${inputCls} resize-y`} placeholder="Tell us briefly what you're planning — big or small — where the project is and what stage you're at. Even 'I have a small deck I'd like rebuilt' is perfect." />
        </div>
      </div>

      <input type="text" name="website" value={fields.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div id="upload" className="mt-8 scroll-mt-28">
        <p className="font-display font-semibold text-lg text-ink mb-1">Already Have Plans? Send Them Through.</p>
        <p className="text-sm text-slate2 mb-4">Upload your architectural plans, consent drawings, engineering information, specifications or project photos and send them directly to Razor Build. No plans yet? No problem — just tell us about the project above.</p>
        <div
          data-testid="plan-upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          className={`border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-gold bg-gold/5" : "border-line bg-paper"}`}
        >
          <UploadCloud className={`w-10 h-10 mx-auto mb-3 ${drag ? "text-gold" : "text-slate2/60"}`} />
          <p className="text-sm text-slate2 mb-1">Drag and drop your files here, or</p>
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="upload-plans-btn" className="mt-2 inline-flex items-center gap-2 border border-ink px-5 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase text-ink hover:bg-ink hover:text-white transition-colors">
            <FileText className="w-4 h-4" /> Upload Plans / Drawings
          </button>
          <p className="mt-3 text-xs text-slate2/70">PDF, JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX — up to {MAX_MB}MB per file, up to 10 files</p>
          <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" className="hidden" data-testid="plan-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {files.length > 0 && (
          <ul data-testid="uploaded-files-list" className="mt-4 divide-y divide-line border border-line">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-4 py-3 bg-white">
                <FileText className="w-4 h-4 text-gold shrink-0" />
                <span className="text-sm text-ink truncate flex-1">{f.file.name}</span>
                <span className="font-mono text-xs text-slate2 shrink-0">{fmtSize(f.file.size)}</span>
                {sending ? (
                  progress === 100 ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <Loader2 className="w-4 h-4 text-gold animate-spin shrink-0" />
                ) : (
                  <button type="button" onClick={() => removeFile(f.id)} data-testid={`remove-file-${f.id}`} className="p-1 text-slate2 hover:text-red-600 transition-colors shrink-0" aria-label={`Remove ${f.file.name}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {sending && (
          <div data-testid="upload-progress" className="mt-4">
            <div className="flex justify-between font-mono text-[11px] tracking-wider uppercase text-slate2 mb-1.5">
              <span>{progress < 100 ? "Uploading…" : "Processing your enquiry…"}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1 bg-line overflow-hidden">
              <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      <button type="submit" disabled={sending} data-testid="send-project-btn" className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-ink text-white font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-charcoal disabled:opacity-60 transition-colors">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-gold" />}
        {sending ? "Sending…" : "Send Project to Razor Build"}
      </button>
      <p className="mt-3 text-xs text-slate2/70 text-center">No account needed. Your details go straight to the builder.</p>
    </form>
  );
}
